/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */

import moment from 'moment';

export const ATTENDANCE_REPORT_RETURN_PATH =
  '/time/displayAttendanceReportCriteria';

/** ShiftPermissionRequest::STATUS_APPROVED */
export const SHIFT_PERMISSION_STATUS_APPROVED = 2;

const YMD = 'YYYY-MM-DD';

const isValidYmd = (value: string | null | undefined): value is string => {
  if (!value) {
    return false;
  }
  return moment(value, YMD, true).isValid();
};

/**
 * Query params for leave assign / view when returning to the attendance report.
 * Uses reportStartDate/reportEndDate so leave fromDate/toDate are not confused.
 */
export const attendanceReportReturnQuery = (
  startDate?: string | null,
  endDate?: string | null,
): {
  returnUrl: string;
  reportStartDate?: string;
  reportEndDate?: string;
} => {
  const query: {
    returnUrl: string;
    reportStartDate?: string;
    reportEndDate?: string;
  } = {
    returnUrl: ATTENDANCE_REPORT_RETURN_PATH,
  };

  if (!isValidYmd(startDate) || !isValidYmd(endDate)) {
    return query;
  }

  const startM = moment(startDate, YMD, true);
  const endM = moment(endDate, YMD, true);
  if (startM.isAfter(endM)) {
    return query;
  }

  query.reportStartDate = startM.format(YMD);
  query.reportEndDate = endM.format(YMD);
  return query;
};

/**
 * Query params when opening Employee Shift Permissions from the attendance report
 * popup (approved permissions only for that employee and day).
 */
export const employeeShiftPermissionsFromAttendanceQuery = (
  empNumber: number | string,
  date: string,
  startDate?: string | null,
  endDate?: string | null,
): {
  returnUrl: string;
  reportStartDate?: string;
  reportEndDate?: string;
  empNumber: number | string;
  fromDate: string;
  toDate: string;
  status: number;
} => ({
  ...attendanceReportReturnQuery(startDate, endDate),
  empNumber,
  fromDate: date,
  toDate: date,
  status: SHIFT_PERMISSION_STATUS_APPROVED,
});

/**
 * Full return path including startDate/endDate for direct navigation after save.
 */
export const buildAttendanceReportReturnUrl = (
  startDate?: string | null,
  endDate?: string | null,
): string => {
  const {returnUrl, reportStartDate, reportEndDate} =
    attendanceReportReturnQuery(startDate, endDate);
  if (reportStartDate && reportEndDate) {
    return `${returnUrl}?startDate=${reportStartDate}&endDate=${reportEndDate}`;
  }
  return returnUrl;
};
